import {DomainObject} from 'shared/store';
import {observable, computed, action, makeObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import {Employee} from 'main/stores/employees';
import Shift from './Shift';
import TimeOffPeriod from './TimeOffPeriod';

class TimeTrackingSchedule extends DomainObject {
  id;
  startDate;
  endDate;
  employees = [];
  scheduleNotificationRecipients = [];
  shifts = [];
  timeOffEvents = [];

  _relations = {
    employees: [Employee],
    scheduleNotificationRecipients: [Employee],
    shifts: [Shift],
    timeOffEvents: [TimeOffPeriod]
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      startDate: observable,
      endDate: observable,
      employees: observable,
      scheduleNotificationRecipients: observable,
      shifts: observable,
      timeOffEvents: observable,
      sortedEmployees: computed,
      firstDay: computed,
      lastDay: computed,
      shiftsByEmployee: computed,
      shiftsByEmployeeAndDay: computed,
      scheduledHoursByEmployee: computed,
      scheduledHours: computed,
      unpublishedShifts: computed,
      notifiesOnPublish: computed,
      replaceShiftsFor: action
    });

    this.merge(other);
  }

  get sortedEmployees() {
    return _.sortBy(this.employees, 'name');
  }

  get firstDay() {
    return moment(this.startDate, 'YYYY-MM-DD');
  }

  get lastDay() {
    return moment(this.endDate, 'YYYY-MM-DD');
  }

  get shiftsByEmployee() {
    return _.groupBy(this.shifts, 'employeeId');
  }

  get shiftsByEmployeeAndDay() {
    return _.mapValues(this.shiftsByEmployee, employeeShifts =>
      _.groupBy(_.sortBy(employeeShifts, 'startTimeFromString'), shift => moment(shift.date).format('YYYY-MM-DD'))
    );
  }

  get scheduledHoursByEmployee() {
    return _.mapValues(this.shiftsByEmployee, employeeShifts => _.sumBy(employeeShifts, 'paidHours'));
  }

  get scheduledHours() {
    return _.sumBy(this.shifts, 'paidHours');
  }

  get unpublishedShifts() {
    return this.shifts.filter(shift => !shift.published);
  }

  get notifiesOnPublish() {
    return _.some(this.unpublishedShifts, shift => this.sendsNotificationsTo(shift.employeeId));
  }

  shiftsFor(employeeId, day) {
    return _.get(this.shiftsByEmployeeAndDay, [employeeId, day.format('YYYY-MM-DD')], []);
  }

  unpublishedShiftsFor(employeeId) {
    return this.unpublishedShifts.filter(shift => shift.employeeId === employeeId);
  }

  scheduledHoursFor(employeeId) {
    return this.scheduledHoursByEmployee[employeeId] || 0;
  }

  scheduledHoursOn(day) {
    return _.sumBy(this.shifts.filter(shift => moment(shift.date).isSame(day, 'day')), 'paidHours');
  }

  sendsNotificationsTo(employeeId) {
    return _.some(this.scheduleNotificationRecipients, {id: employeeId});
  }

  isOverlapping(shift) {
    return _.some(this.shifts, other =>
      other !== shift && other.employeeId === shift.employeeId && shift.overlaps(other.startTimeFromString, other.endTimeFromString)
    ) || _.some(this.timeOffEvents, timeOff => timeOff.employeeId === shift.employeeId && shift.overlaps(timeOff.startsAt, timeOff.endsAt));
  }

  timeOffSegmentsFor(employeeId, day) {
    return _.sortBy(this.timeOffEvents, timeOff => timeOff.startsAt.valueOf()).
      filter(timeOff => timeOff.employeeId === employeeId && timeOff.coversDay(day)).
      map(timeOff => ({
        timeOff,
        continuesBefore: timeOff.continuesBefore(day) && !day.isSame(this.firstDay, 'day'),
        continuesAfter: timeOff.continuesAfter(day) && !day.isSame(this.lastDay, 'day')
      }));
  }

  replaceShiftsFor(employeeIds, shifts) {
    const replacedShiftIds = _.map(shifts, 'id');

    this.shifts = [
      ..._.reject(this.shifts, shift => _.includes(employeeIds, shift.employeeId) || _.includes(replacedShiftIds, shift.id)),
      ...shifts
    ];
  }
}

export default TimeTrackingSchedule;
